import React from 'react';
import { cn } from '../../lib/utils';

function Bone({ className }) {
  return <div className={cn('bg-slate-200/70 rounded animate-pulse', className)} />;
}

export function StatCardsSkeleton({ count = 8 }) {
  return (
    <div className={cn('grid gap-3', `grid-cols-2 md:grid-cols-4 lg:grid-cols-${Math.min(count, 8)}`)} data-testid="stat-cards-skeleton">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="bg-white rounded-xl border p-4 space-y-2.5">
          <div className="flex items-center justify-between">
            <Bone className="h-2.5 w-16" />
            <Bone className="h-4 w-4 rounded-sm" />
          </div>
          <Bone className="h-7 w-14" />
          <Bone className="h-2 w-20" />
        </div>
      ))}
    </div>
  );
}

export function TableSkeleton({ rows = 6, cols = 5 }) {
  return (
    <div className="bg-white rounded-xl border overflow-hidden" data-testid="table-skeleton">
      <div className="px-5 py-3 border-b flex items-center gap-3">
        <Bone className="h-4 w-32" />
        <div className="flex-1" />
        <Bone className="h-8 w-24 rounded-md" />
      </div>
      <div className="divide-y">
        {Array.from({ length: rows }).map((_, i) => (
          <div key={i} className="flex items-center gap-4 px-5 py-3" style={{ opacity: 1 - i * 0.1 }}>
            {Array.from({ length: cols }).map((_, j) => (
              <Bone key={j} className={cn('h-3', j === 0 ? 'w-28' : j === cols - 1 ? 'w-16' : 'w-16')} />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

export function CallTrackerSkeleton() {
  return (
    <div className="space-y-4 animate-in fade-in-0 duration-300" data-testid="call-tracker-skeleton">
      {/* Filter bar */}
      <div className="flex items-center gap-3">
        <Bone className="h-9 w-48 rounded-lg" />
        <Bone className="h-9 w-32 rounded-lg" />
        <Bone className="h-9 w-32 rounded-lg" />
        <div className="flex-1" />
        <Bone className="h-9 w-20 rounded-lg" />
      </div>
      {/* Call rows */}
      <div className="space-y-1">
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={i} className="bg-white rounded-lg border px-4 py-3 flex items-center gap-4" style={{ opacity: 1 - i * 0.08 }}>
            <Bone className="h-4 w-4 rounded-full" />
            <Bone className="h-4 w-28" />
            <Bone className="h-4 w-20" />
            <div className="flex-1" />
            <Bone className="h-6 w-16 rounded-full" />
            <Bone className="h-6 w-16 rounded-full" />
            <Bone className="h-4 w-12" />
          </div>
        ))}
      </div>
    </div>
  );
}

export function DashboardSkeleton() {
  return (
    <div className="space-y-4 animate-in fade-in-0 duration-300" data-testid="dashboard-skeleton">
      <StatCardsSkeleton count={8} />
      <StatCardsSkeleton count={8} />
      <div className="grid grid-cols-2 gap-4">
        <div className="bg-white rounded-xl border p-5 space-y-3">
          <Bone className="h-3 w-28" />
          <div className="flex items-center gap-3">
            <Bone className="h-10 w-10 rounded-full" />
            <div className="space-y-1.5">
              <Bone className="h-3.5 w-24" />
              <Bone className="h-2.5 w-36" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border p-5 space-y-3">
          <Bone className="h-3 w-28" />
          <div className="flex items-center gap-3">
            <Bone className="h-10 w-10 rounded-full" />
            <div className="space-y-1.5">
              <Bone className="h-3.5 w-24" />
              <Bone className="h-2.5 w-36" />
            </div>
          </div>
        </div>
      </div>
      <TableSkeleton rows={4} cols={8} />
    </div>
  );
}

export function LeadsSkeleton() {
  return (
    <div className="space-y-4 animate-in fade-in-0 duration-300" data-testid="leads-skeleton">
      {/* Metrics bar */}
      <div className="flex gap-2">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="bg-white rounded-lg border px-4 py-2.5 flex-1 space-y-1.5">
            <Bone className="h-2.5 w-14" />
            <Bone className="h-5 w-8" />
          </div>
        ))}
      </div>
      {/* Filter bar */}
      <div className="flex items-center gap-3">
        <Bone className="h-9 w-48 rounded-lg" />
        <Bone className="h-9 w-32 rounded-lg" />
        <div className="flex-1" />
        <Bone className="h-9 w-20 rounded-lg" />
      </div>
      {/* Lead rows */}
      <div className="space-y-1.5">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="bg-white rounded-lg border px-4 py-3 flex items-center gap-4" style={{ opacity: 1 - i * 0.1 }}>
            <Bone className="h-9 w-9 rounded-full" />
            <div className="space-y-1">
              <Bone className="h-3.5 w-28" />
              <Bone className="h-2.5 w-20" />
            </div>
            <div className="flex-1" />
            <Bone className="h-6 w-16 rounded-full" />
            <Bone className="h-6 w-20 rounded-full" />
            <Bone className="h-4 w-16" />
          </div>
        ))}
      </div>
    </div>
  );
}

export function SchedulingSkeleton() {
  return (
    <div className="space-y-3 animate-in fade-in-0 duration-300" data-testid="scheduling-skeleton">
      {/* Header with arrows */}
      <div className="flex items-center gap-3">
        <Bone className="h-9 w-9 rounded-full" />
        <Bone className="h-5 w-32" />
        <Bone className="h-9 w-9 rounded-full" />
        <div className="flex-1" />
        <Bone className="h-9 w-24 rounded-lg" />
      </div>
      {/* Calendar grid */}
      <div className="bg-white rounded-xl border overflow-hidden">
        <div className="grid grid-cols-6 gap-px bg-slate-200">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="bg-slate-50 px-3 py-2 text-center">
              <Bone className="h-3 w-16 mx-auto" />
            </div>
          ))}
        </div>
        <div className="grid grid-cols-6 gap-px bg-slate-100 min-h-[400px]">
          {Array.from({ length: 18 }).map((_, i) => (
            <div key={i} className="bg-white p-2">
              {Math.random() > 0.5 && <Bone className="h-8 w-full rounded" />}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function GenericPageSkeleton() {
  return (
    <div className="space-y-4 animate-in fade-in-0 duration-300">
      <div className="flex items-center justify-between">
        <div className="space-y-2">
          <Bone className="h-6 w-40" />
          <Bone className="h-3 w-56" />
        </div>
        <Bone className="h-9 w-28 rounded-lg" />
      </div>
      <StatCardsSkeleton count={4} />
      <TableSkeleton />
    </div>
  );
}
